// Fixed celebration-banner + desktop header stack height (lg+), and the mobile header bar height.
const DESKTOP_FIXED_STACK_OFFSET = 190;
const MOBILE_FIXED_HEADER_OFFSET = 80;

// The target element usually exists in the DOM well before the image/video/slider-heavy
// content above it has loaded and stopped reflowing. So instead of scrolling once, we
// re-align to the target on every animation frame until its position holds steady for a
// few consecutive frames with the document fully loaded, or until the timeout elapses.
const SETTLE_TIMEOUT_MS = 3000;
const NOT_FOUND_TIMEOUT_MS = 1500;
const STABLE_FRAMES_REQUIRED = 3;
const STABLE_TOLERANCE_PX = 1;

// Keys that scroll the page — pressing one means the user has taken over.
const SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
  "Spacebar",
]);

// Scrolls to the element with the given id, keeping it aligned (accounting for the fixed
// banner/header stack) while the page finishes loading. Any real scroll gesture from the
// user cancels the remaining re-alignment so we never fight them. Returns a cancel function.
export function scrollToHashTarget(id) {
  const startTime = performance.now();
  let rafId = null;
  let cancelled = false;
  let everFound = false;
  let stableFrames = 0;
  let lastTop = null;

  const stop = () => {
    if (cancelled) return;
    cancelled = true;
    if (rafId !== null) cancelAnimationFrame(rafId);
    window.removeEventListener("wheel", stop);
    window.removeEventListener("touchmove", stop);
    window.removeEventListener("keydown", onKeyDown);
  };

  const onKeyDown = (event) => {
    if (SCROLL_KEYS.has(event.key)) stop();
  };

  const tick = () => {
    if (cancelled) return;

    const target = document.getElementById(id);
    if (target) {
      everFound = true;

      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      const offset = isDesktop ? DESKTOP_FIXED_STACK_OFFSET : MOBILE_FIXED_HEADER_OFFSET;
      const top = Math.max(
        0,
        target.getBoundingClientRect().top + window.pageYOffset - offset
      );
      window.scrollTo({ top, left: 0, behavior: "instant" });

      stableFrames =
        lastTop !== null && Math.abs(top - lastTop) <= STABLE_TOLERANCE_PX
          ? stableFrames + 1
          : 0;
      lastTop = top;

      if (
        document.readyState === "complete" &&
        stableFrames >= STABLE_FRAMES_REQUIRED
      ) {
        return stop();
      }
    }

    const elapsed = performance.now() - startTime;
    if (
      elapsed >= SETTLE_TIMEOUT_MS ||
      (!everFound && elapsed >= NOT_FOUND_TIMEOUT_MS)
    ) {
      return stop();
    }

    rafId = requestAnimationFrame(tick);
  };

  window.addEventListener("wheel", stop, { passive: true });
  window.addEventListener("touchmove", stop, { passive: true });
  window.addEventListener("keydown", onKeyDown);

  rafId = requestAnimationFrame(tick);

  return stop;
}
